import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { FirestoreService } from '@/services/firestoreService'
import { UploadProcessingService } from '@/services/uploadProcessingService'
import { StagingEarningsProcessingService } from '@/services/stagingEarningsProcessingService'
import { ColumnMappingService } from '@/services/columnMappingService'
import { DataExtractionService } from '@/services/dataExtractionService'
import { DriverMasterService } from '@/services/driverMasterService'
import { PlatformDetectionService } from '@/services/platformDetectionService'
import { useUploadStore } from '@/stores/uploadStore'
import {
  Upload,
  UploadStaging,
  Platform,
  DriverMatchResult,
  SimpleProcessingProgress,
  PlatformDetectionResult
} from '@/types'
import { DriverMaster } from '@/types/driverMaster'
import {
  DriverActionResults,
  EarningsCreationResults
} from '@/services/stagingEarningsProcessingService'

export type WizardUploadSubStep =
  | 'file-select'
  | 'field-mapping'
  | 'driver-review'
  | 'creating-earnings'
  | 'complete'

export interface WizardUploadState {
  subStep: WizardUploadSubStep
  isLoading: boolean

  // Existing uploads for this period
  existingUploads: Upload[]
  existingUploadsLoading: boolean

  // Current upload being processed
  currentUpload: Upload | null
  uploadStaging: UploadStaging | null

  // Platform detection
  detectedPlatform: Platform | null
  platformConfidence: number
  showPlatformDialog: boolean

  // Field mapping
  identifierMapping: Record<string, string>
  fieldMapping: Record<string, string>
  shouldSkipFieldMapping: boolean

  // Driver matching
  driverMatches: DriverMatchResult[]
  availableDrivers: DriverMaster[]
  unmatchedActions: Map<number, any>
  perfectMatchOverrides: Map<number, any>
  ignoredRows: Set<number>
  autoAcceptMatches: boolean

  // Processing
  processingProgress: SimpleProcessingProgress | null
  driverActionResults: DriverActionResults | null
  earningsResults: EarningsCreationResults | null

  // Errors
  error: string | null
}

const initialState: WizardUploadState = {
  subStep: 'file-select',
  isLoading: false,
  existingUploads: [],
  existingUploadsLoading: true,
  currentUpload: null,
  uploadStaging: null,
  detectedPlatform: null,
  platformConfidence: 0,
  showPlatformDialog: false,
  identifierMapping: {},
  fieldMapping: {},
  shouldSkipFieldMapping: false,
  driverMatches: [],
  availableDrivers: [],
  unmatchedActions: new Map(),
  perfectMatchOverrides: new Map(),
  ignoredRows: new Set(),
  autoAcceptMatches: true,
  processingProgress: null,
  driverActionResults: null,
  earningsResults: null,
  error: null
}

export interface WizardUploadController {
  state: WizardUploadState
  updateState: (updates: Partial<WizardUploadState>) => void

  // Actions
  loadExistingUploads: () => Promise<void>
  handleFileUpload: (file: File) => Promise<void>
  confirmPlatform: (platform: Platform) => void
  cancelPlatformSelection: () => void
  runFieldMapping: () => Promise<void>
  processDriverActionsAndCreateEarnings: () => Promise<void>
  deleteUpload: (uploadId: string) => Promise<void>

  // Driver review actions
  onUnmatchedActionChange: (rowIndex: number, action: 'create_new' | 'select_existing', data?: { selectedDriverId?: string; newDriverName?: string }) => void
  onPerfectMatchOverrideChange: (rowIndex: number, action: 'create_new' | 'select_existing', data?: { selectedDriverId?: string; newDriverName?: string }) => void
  onRemovePerfectMatchOverride: (rowIndex: number) => void
  onIgnoreRow: (rowIndex: number) => void
  onUnignoreRow: (rowIndex: number) => void
  onAutoAcceptMatchesChange: (autoAccept: boolean) => void

  // Navigation
  goToSubStep: (step: WizardUploadSubStep) => void
  startNewUpload: () => void
  canContinueToAdjustments: () => boolean
}

export function useWizardUploadController(periodId: string): WizardUploadController {
  const { currentUser } = useAuth()
  const [state, setState] = useState<WizardUploadState>(initialState)
  const { uploadFile } = useUploadStore()

  const stateRef = useRef(state)
  useEffect(() => {
    stateRef.current = state
  }, [state])

  const updateState = useCallback((updates: Partial<WizardUploadState>) => {
    setState(prev => ({ ...prev, ...updates }))
  }, [])

  const updateProcessingProgress = useCallback((progress: SimpleProcessingProgress | null) => {
    updateState({ processingProgress: progress })
  }, [updateState])

  // Load existing uploads for this period
  const loadExistingUploads = useCallback(async () => {
    if (!currentUser) return

    updateState({ existingUploadsLoading: true })

    try {
      const firestoreService = new FirestoreService(currentUser)
      const uploads = await firestoreService.getUploadsByPeriod(periodId)
      // Filter to only show completed uploads (not pending/failed)
      const completedUploads = uploads.filter(u =>
        u.status === 'processed' || u.status === 'earnings_created'
      )
      updateState({ existingUploads: completedUploads, existingUploadsLoading: false })
    } catch (error) {
      console.error('[WizardUpload] Failed to load uploads:', error)
      updateState({ existingUploadsLoading: false, error: 'Kunde inte ladda befintliga uppladdningar' })
    }
  }, [currentUser, periodId, updateState])

  // Handle file upload and platform detection
  const handleFileUpload = useCallback(async (file: File) => {
    if (!currentUser) return

    updateState({ isLoading: true, error: null })

    try {
      // Upload file via store (this parses and stores the raw data)
      const uploadId = await uploadFile(file, periodId, '', currentUser)

      if (!uploadId) {
        throw new Error('Upload failed - no ID returned')
      }

      // Get the upload to access headers for platform detection
      const firestoreService = new FirestoreService(currentUser)
      const upload = await firestoreService.getUpload(uploadId)

      if (!upload) {
        throw new Error('Could not load uploaded file')
      }

      // Auto-detect platform from headers
      const detection = PlatformDetectionService.detectPlatformFromFile(upload.headers)
      const confidence = detection.confidence

      if (confidence >= 80 && detection.detectedPlatform) {
        // High confidence - auto-select and proceed
        const platform = detection.detectedPlatform as Platform
        await initializeStaging(upload, platform, firestoreService)
      } else {
        // Low confidence - show platform selection dialog
        updateState({
          currentUpload: upload,
          detectedPlatform: detection.suggestedDefault as Platform || null,
          platformConfidence: confidence,
          showPlatformDialog: true,
          isLoading: false
        })
      }
    } catch (error) {
      console.error('[WizardUpload] Upload failed:', error)
      updateState({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Uppladdning misslyckades'
      })
    }
  }, [currentUser, periodId, uploadFile, updateState])

  // Initialize staging after platform is confirmed
  const initializeStaging = useCallback(async (
    upload: Upload,
    platform: Platform,
    firestoreService: FirestoreService
  ) => {
    if (!currentUser) return

    try {
      // Update upload with platform
      await firestoreService.updateUpload(upload.id, { platform, period: periodId })
      const updatedUpload = { ...upload, platform, period: periodId }

      // Create staging
      const platformInfo: PlatformDetectionResult = {
        detectedPlatform: platform,
        confidence: 100,
        allMatches: [],
        shouldAutoSelect: true
      }

      const stagingData = DataExtractionService.createUploadStaging(
        upload.id,
        platform,
        periodId,
        [],
        platformInfo
      )

      const stagingId = await firestoreService.createUploadStaging(stagingData)
      const staging = await firestoreService.getUploadStaging(stagingId)

      if (!staging) {
        throw new Error('Could not create staging')
      }

      // Get initial mappings
      const { identifierMapping, fieldMapping, shouldSkip } = getInitialMappings(staging, updatedUpload)

      // Load available drivers
      const drivers = await DriverMasterService.getDrivers(currentUser)

      updateState({
        currentUpload: updatedUpload,
        uploadStaging: staging,
        identifierMapping,
        fieldMapping,
        shouldSkipFieldMapping: shouldSkip,
        availableDrivers: drivers,
        showPlatformDialog: false,
        isLoading: false,
        subStep: shouldSkip ? 'driver-review' : 'field-mapping'
      })

      // If we can skip field mapping, run it automatically then do driver matching
      if (shouldSkip) {
        await runFieldMappingInternal(staging, updatedUpload, identifierMapping, fieldMapping)
      }
    } catch (error) {
      console.error('[WizardUpload] Staging initialization failed:', error)
      updateState({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Kunde inte förbereda uppladdningen'
      })
    }
  }, [currentUser, periodId, updateState])

  // Confirm platform selection from dialog
  const confirmPlatform = useCallback(async (platform: Platform) => {
    const currentState = stateRef.current
    if (!currentUser || !currentState.currentUpload) return

    updateState({ showPlatformDialog: false, isLoading: true })

    const firestoreService = new FirestoreService(currentUser)
    await initializeStaging(currentState.currentUpload, platform, firestoreService)
  }, [currentUser, initializeStaging, updateState])

  const cancelPlatformSelection = useCallback(() => {
    updateState({
      showPlatformDialog: false,
      currentUpload: null,
      isLoading: false
    })
  }, [updateState])

  // Run field mapping
  const runFieldMappingInternal = useCallback(async (
    staging: UploadStaging,
    upload: Upload,
    idMapping: Record<string, string>,
    fMapping: Record<string, string>
  ) => {
    if (!currentUser) return

    try {
      const firestoreService = new FirestoreService(currentUser)

      // Validate and prepare column mappings
      const {
        columnMapping,
        missingRequiredFields,
        duplicateHeaders
      } = ColumnMappingService.validateAndPrepareMappings(
        idMapping,
        fMapping,
        upload.headers,
        staging.platform
      )

      if (missingRequiredFields.length > 0) {
        updateState({ error: `Obligatoriska fält saknas: ${missingRequiredFields.join(', ')}` })
        return
      }

      if (duplicateHeaders.length > 0) {
        updateState({ error: `Samma kolumn har valts flera gånger: ${duplicateHeaders.join(', ')}` })
        return
      }

      // Save column mappings
      await firestoreService.updateUploadStaging(staging.id, {
        columnMapping,
        updatedAt: new Date()
      })

      // Extract data using validated mappings
      const extractionResult = DataExtractionService.extractData(
        upload.data,
        upload.headers,
        staging.platformInfo,
        columnMapping,
        staging.platform
      )

      // Save extracted data
      await firestoreService.updateUploadStaging(staging.id, {
        normalizedData: extractionResult.normalizedData,
        updatedAt: new Date()
      })

      const updatedStaging = {
        ...staging,
        columnMapping,
        normalizedData: extractionResult.normalizedData
      }

      updateState({
        uploadStaging: updatedStaging,
        identifierMapping: columnMapping.identifierFields,
        fieldMapping: columnMapping.earningsFields
      })

      // Run driver matching
      await runDriverMatching(updatedStaging)
    } catch (error) {
      console.error('[WizardUpload] Field mapping failed:', error)
      updateState({ error: error instanceof Error ? error.message : 'Fältmappning misslyckades' })
    }
  }, [currentUser, updateState])

  const runFieldMapping = useCallback(async () => {
    const currentState = stateRef.current
    if (!currentState.uploadStaging || !currentState.currentUpload) return

    updateState({ isLoading: true, error: null })

    await runFieldMappingInternal(
      currentState.uploadStaging,
      currentState.currentUpload,
      currentState.identifierMapping,
      currentState.fieldMapping
    )

    updateState({ isLoading: false, subStep: 'driver-review' })
  }, [runFieldMappingInternal, updateState])

  // Run driver matching
  const runDriverMatching = useCallback(async (staging: UploadStaging) => {
    if (!currentUser || !staging.normalizedData?.length) return

    updateState({ isLoading: true })

    try {
      const uploadProcessingService = new UploadProcessingService(currentUser)

      await uploadProcessingService.processUploadStaging(
        staging.id,
        (progress) => {
          updateProcessingProgress({
            current: progress.processedRows,
            total: progress.totalRows,
            percentage: progress.percentage,
            status: progress.currentStatus,
            estimatedTimeRemaining: progress.estimatedTimeRemaining
          })
        }
      )

      const firestoreService = new FirestoreService(currentUser)
      const updatedStaging = await firestoreService.getUploadStaging(staging.id)

      if (updatedStaging) {
        const driverMatches = updatedStaging.driverMatches || []

        updateState({
          uploadStaging: updatedStaging,
          driverMatches,
          unmatchedActions: getDefaultUnmatchedActions(driverMatches),
          isLoading: false,
          processingProgress: null
        })
      }
    } catch (error) {
      console.error('[WizardUpload] Driver matching failed:', error)
      updateState({
        isLoading: false,
        processingProgress: null,
        error: error instanceof Error ? error.message : 'Förarmatchning misslyckades'
      })
    }
  }, [currentUser, updateState, updateProcessingProgress])

  // Process driver actions and create earnings (combined - no confirmation step)
  const processDriverActionsAndCreateEarnings = useCallback(async () => {
    if (!currentUser) return
    const currentState = stateRef.current
    if (!currentState.uploadStaging) return

    updateState({ subStep: 'creating-earnings', isLoading: true, error: null })

    try {
      const processingService = new StagingEarningsProcessingService(currentUser)

      // Process driver actions first
      const driverActionResults = await processingService.processDriverActions(
        currentState.uploadStaging.id,
        {
          unmatchedActions: currentState.unmatchedActions,
          perfectMatchOverrides: currentState.perfectMatchOverrides,
          ignoredRows: currentState.ignoredRows
        },
        (progress) => {
          updateProcessingProgress({
            current: progress.processedRows,
            total: progress.totalRows,
            percentage: progress.percentage,
            status: progress.currentStatus,
            estimatedTimeRemaining: progress.estimatedTimeRemaining
          })
        }
      )

      updateState({ driverActionResults })

      // Then create earnings
      const earningsResults = await processingService.createEarningsFromStaging(
        currentState.uploadStaging.id,
        currentState.ignoredRows,
        (progress) => {
          updateProcessingProgress({
            current: progress.processedRows,
            total: progress.totalRows,
            percentage: progress.percentage,
            status: progress.currentStatus,
            estimatedTimeRemaining: progress.estimatedTimeRemaining
          })
        }
      )

      updateState({
        earningsResults,
        isLoading: false,
        processingProgress: null,
        subStep: 'complete'
      })

      // Refresh existing uploads list
      await loadExistingUploads()
    } catch (error) {
      console.error('[WizardUpload] Earnings creation failed:', error)
      updateState({
        isLoading: false,
        processingProgress: null,
        subStep: 'driver-review',
        error: error instanceof Error ? error.message : 'Kunde inte skapa inkomster'
      })
    }
  }, [currentUser, updateState, updateProcessingProgress, loadExistingUploads])

  // Delete an upload
  const deleteUpload = useCallback(async (uploadId: string) => {
    if (!currentUser) return

    try {
      const firestoreService = new FirestoreService(currentUser)
      // Delete earnings associated with this upload
      await firestoreService.deleteEarningsByUploadId(uploadId)
      // Delete the upload itself
      await firestoreService.deleteUpload(uploadId)
      // Refresh list
      await loadExistingUploads()
    } catch (error) {
      console.error('[WizardUpload] Delete failed:', error)
      updateState({ error: 'Kunde inte ta bort uppladdningen' })
    }
  }, [currentUser, loadExistingUploads, updateState])

  // Navigation
  const goToSubStep = useCallback((step: WizardUploadSubStep) => {
    updateState({ subStep: step })
  }, [updateState])

  const startNewUpload = useCallback(() => {
    setState({
      ...initialState,
      existingUploads: stateRef.current.existingUploads,
      existingUploadsLoading: false
    })
  }, [])

  const canContinueToAdjustments = useCallback(() => {
    return state.existingUploads.length > 0
  }, [state.existingUploads.length])

  // Driver review action callbacks
  const onUnmatchedActionChange = useCallback((
    rowIndex: number,
    action: 'create_new' | 'select_existing',
    data?: { selectedDriverId?: string; newDriverName?: string }
  ) => {
    setState(prev => {
      const newActions = new Map(prev.unmatchedActions)
      newActions.set(rowIndex, { rowIndex, action, ...data })
      return { ...prev, unmatchedActions: newActions }
    })
  }, [])

  const onPerfectMatchOverrideChange = useCallback((
    rowIndex: number,
    action: 'create_new' | 'select_existing',
    data?: { selectedDriverId?: string; newDriverName?: string }
  ) => {
    setState(prev => {
      const newOverrides = new Map(prev.perfectMatchOverrides)
      newOverrides.set(rowIndex, { rowIndex, action, ...data })
      return { ...prev, perfectMatchOverrides: newOverrides }
    })
  }, [])

  const onRemovePerfectMatchOverride = useCallback((rowIndex: number) => {
    setState(prev => {
      const newOverrides = new Map(prev.perfectMatchOverrides)
      newOverrides.delete(rowIndex)
      return { ...prev, perfectMatchOverrides: newOverrides }
    })
  }, [])

  const onIgnoreRow = useCallback((rowIndex: number) => {
    setState(prev => {
      const newIgnored = new Set(prev.ignoredRows)
      newIgnored.add(rowIndex)
      return { ...prev, ignoredRows: newIgnored }
    })
  }, [])

  const onUnignoreRow = useCallback((rowIndex: number) => {
    setState(prev => {
      const newIgnored = new Set(prev.ignoredRows)
      newIgnored.delete(rowIndex)
      return { ...prev, ignoredRows: newIgnored }
    })
  }, [])

  const onAutoAcceptMatchesChange = useCallback((autoAccept: boolean) => {
    updateState({ autoAcceptMatches: autoAccept })
  }, [updateState])

  // Load existing uploads on mount
  useEffect(() => {
    if (currentUser && periodId) {
      loadExistingUploads()
    }
  }, [currentUser, periodId, loadExistingUploads])

  return useMemo(() => ({
    state,
    updateState,
    loadExistingUploads,
    handleFileUpload,
    confirmPlatform,
    cancelPlatformSelection,
    runFieldMapping,
    processDriverActionsAndCreateEarnings,
    deleteUpload,
    onUnmatchedActionChange,
    onPerfectMatchOverrideChange,
    onRemovePerfectMatchOverride,
    onIgnoreRow,
    onUnignoreRow,
    onAutoAcceptMatchesChange,
    goToSubStep,
    startNewUpload,
    canContinueToAdjustments
  }), [
    state,
    updateState,
    loadExistingUploads,
    handleFileUpload,
    confirmPlatform,
    cancelPlatformSelection,
    runFieldMapping,
    processDriverActionsAndCreateEarnings,
    deleteUpload,
    onUnmatchedActionChange,
    onPerfectMatchOverrideChange,
    onRemovePerfectMatchOverride,
    onIgnoreRow,
    onUnignoreRow,
    onAutoAcceptMatchesChange,
    goToSubStep,
    startNewUpload,
    canContinueToAdjustments
  ])
}

function getInitialMappings(staging: UploadStaging, upload: Upload) {
  if (staging.columnMapping?.identifierFields && staging.columnMapping?.earningsFields) {
    const platformConfig = ColumnMappingService.getConfigForPlatform(upload.platform as Platform)
    const allPlatformFields = [
      ...Object.keys(platformConfig?.identifierFields || {}),
      ...Object.keys(platformConfig?.earningsFields || {})
    ]

    const allMappedFields = [
      ...Object.keys(staging.columnMapping.identifierFields),
      ...Object.keys(staging.columnMapping.earningsFields)
    ]

    const allAvailableFieldsMapped = allPlatformFields.every(field => allMappedFields.includes(field))
    const shouldSkip =
      (staging.columnMapping.missingRequiredFields?.length ?? 1) === 0 &&
      allAvailableFieldsMapped &&
      (staging.columnMapping.confidence ?? 0) >= 95

    return {
      identifierMapping: staging.columnMapping.identifierFields,
      fieldMapping: staging.columnMapping.earningsFields,
      shouldSkip
    }
  }

  return {
    identifierMapping: {},
    fieldMapping: {},
    shouldSkip: false
  }
}

function getDefaultUnmatchedActions(driverMatches: DriverMatchResult[]): Map<number, any> {
  const actions = new Map<number, any>()

  for (const match of driverMatches) {
    if (match.hasMatch) continue

    const newDriverName =
      match.extractedName ||
      match.identifiers?.name ||
      `Förare ${match.rowIndex + 1}`

    actions.set(match.rowIndex, {
      rowIndex: match.rowIndex,
      action: 'create_new',
      newDriverName
    })
  }

  return actions
}
